import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import apiService from '../utils/apiService';

/**
 * Real-time training job listener hook
 * @param {string} userId - User ID
 * @returns {Object} - { trainingJob, loading, error }
 */
export const useTrainingJobListener = (userId) => {
  const [trainingJob, setTrainingJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    console.log('🔄 Setting up real-time training job listener...');

    // Query for the most recent training job
    const trainingJobsQuery = query(
      collection(db, 'trainingJobs'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(1)
    );

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      trainingJobsQuery,
      async (querySnapshot) => {
        try {
          if (querySnapshot.empty) {
            console.log('📭 No training jobs found');
            setTrainingJob(null);
            setLoading(false);
            return;
          }

          const jobData = {
            id: querySnapshot.docs[0].id,
            ...querySnapshot.docs[0].data()
          };

          console.log('📄 Training job updated:', jobData.status);

          // If job is in progress, fetch latest status from backend
          if (['starting', 'queued', 'training'].includes(jobData.status) && jobData.replicateJobId) {
            console.log('🔄 Fetching latest status from backend...');
            try {
              const backendStatus = await apiService.getTrainingStatus(userId);
              setTrainingJob(backendStatus);
            } catch (backendError) {
              console.warn('Backend status failed, using Firestore data:', backendError.message);
              setTrainingJob(jobData);
            }
          } else {
            // Job completed/failed, use Firestore data
            setTrainingJob(jobData);
          }

          setLoading(false);
        } catch (err) {
          console.error('Error processing training job update:', err);
          setError(err.message);
          setLoading(false);
        }
      },
      (err) => {
        console.error('Training job listener error:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => {
      console.log('🔌 Cleaning up training job listener');
      unsubscribe();
    };
  }, [userId]);

  return { trainingJob, loading, error };
};